import {
  Component,
  Input,
  signal,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

import { Taxonomy, isTaxonomyPublished } from '../taxonomy.model';
import { ConfirmPopupService } from '../../../services/confirm-popup.service';
import { PopupConfirmationData } from '../../../shared/constant';
import { TaxonomyService } from '../../../services/taxonomy.service';
import { TaxonomyApiService } from '../../../services/api-services/taxonomy-api.service';
import { CustomDateTimePipe } from '../../../pipes/custom-date-time.pipe';
import { Router } from '@angular/router';
import { ActionService } from '../../../services/action.service';
import { TaxonomyConfigureComponent } from '../taxonomy-configure/taxonomy-configure.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClonePopupService } from '../../../services/clone-popup.service';
import { NotificationService } from '../../../services/notification.service';

@Component({
  selector: 'taxonomy-card',
  standalone: true,
  imports: [CommonModule, CustomDateTimePipe, TaxonomyConfigureComponent, MatTooltipModule, MatMenuModule],
  providers: [DatePipe],
  templateUrl: './taxonomy-card.component.html',
  styleUrl: './taxonomy-card.component.scss',
})
export class TaxonomyCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('taxonomyName', { static: false }) taxonomyNameRef!: ElementRef;
  @ViewChild('taxonomyDescription', { static: false }) taxonomyDescriptionRef!: ElementRef;
  @ViewChild('moreMenuTrigger', { static: false }) moreMenuTrigger!: MatMenuTrigger;

  constructor(
    private confirmPopupService: ConfirmPopupService,
    private taxonomyService: TaxonomyService,
    private taxonomyApiService: TaxonomyApiService,
    private router: Router,
    public actionService: ActionService,
    private cdr: ChangeDetectorRef,
    private clonePopupService: ClonePopupService,
    private notificationService: NotificationService
  ) {}

  @Input() taxonomy!: Taxonomy;

  _showMoreDropdown = signal<boolean>(false);
  _isNameTruncated = signal<boolean>(false);
  _isDescriptionTruncated = signal<boolean>(false);

  private resizeObserver: ResizeObserver | null = null;

  // Computed property to determine if taxonomy is published
  get isPublished(): boolean {
    return isTaxonomyPublished(this.taxonomy);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['taxonomy'] && !changes['taxonomy'].firstChange) {
      // Recheck text overflow when taxonomy data changes
      setTimeout(() => {
        this.checkTextOverflow();
      });
    }
  }

  ngAfterViewInit() {
    // Check for text overflow after view is initialized
    setTimeout(() => {
      this.checkTextOverflow();
      this.setupResizeObserver();
    });
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver() {
    if (this.taxonomyNameRef?.nativeElement || this.taxonomyDescriptionRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkTextOverflow();
      });

      if (this.taxonomyNameRef?.nativeElement) {
        this.resizeObserver.observe(this.taxonomyNameRef.nativeElement);
      }
      if (this.taxonomyDescriptionRef?.nativeElement) {
        this.resizeObserver.observe(this.taxonomyDescriptionRef.nativeElement);
      }
    }
  }

  private checkTextOverflow() {
    // Check taxonomy name overflow
    if (this.taxonomyNameRef?.nativeElement) {
      const nameElement = this.taxonomyNameRef.nativeElement;
      const isNameTruncated = this.isElementTextTruncated(nameElement);
      this._isNameTruncated.set(isNameTruncated);
    }

    // Check taxonomy description overflow
    if (this.taxonomyDescriptionRef?.nativeElement) {
      const descElement = this.taxonomyDescriptionRef.nativeElement;
      const isDescTruncated = this.isElementTextTruncated(descElement);
      this._isDescriptionTruncated.set(isDescTruncated);
    }

    this.cdr.detectChanges();
  }

  private isElementTextTruncated(element: HTMLElement): boolean {
    // Create a temporary element to measure the actual text width
    const tempElement = document.createElement('span');
    const computedStyle = window.getComputedStyle(element);

    // Copy all relevant styles to the temporary element
    tempElement.style.fontFamily = computedStyle.fontFamily;
    tempElement.style.fontSize = computedStyle.fontSize;
    tempElement.style.fontWeight = computedStyle.fontWeight;
    tempElement.style.fontStyle = computedStyle.fontStyle;
    tempElement.style.letterSpacing = computedStyle.letterSpacing;
    tempElement.style.lineHeight = computedStyle.lineHeight;
    tempElement.style.whiteSpace = 'nowrap';
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.pointerEvents = 'none';

    // Set the text content
    tempElement.textContent = element.textContent || '';

    // Add to DOM temporarily
    document.body.appendChild(tempElement);

    // Measure the text width
    const textWidth = tempElement.offsetWidth;

    // Remove from DOM
    document.body.removeChild(tempElement);

    // Get the container width
    const containerWidth = element.offsetWidth;

    // Check if text is truncated
    return textWidth > containerWidth;
  }

  // Method to check if taxonomy name is truncated
  isTaxonomyNameTruncated(): boolean {
    return this._isNameTruncated();
  }

  // Method to check if taxonomy description is truncated
  isTaxonomyDescriptionTruncated(): boolean {
    return this._isDescriptionTruncated();
  }

  // Method to get the published version number
  getPublishedVersionNumber(): string {
    if (!this.taxonomy.version_history_details || !Array.isArray(this.taxonomy.version_history_details)) {
      return '';
    }

    const publishedVersion = this.taxonomy.version_history_details.find(
      (version: any) => version.status === 'published'
    );

    return publishedVersion?.version ? `Version ${publishedVersion.version}` : '';
  }

  // Method to get the tag text based on DraftVisibleToAll and published status
  getTagText(): string {
    if (this.isPublished) {
      return `Published • ${this.getPublishedVersionNumber()}`;
    }

    // Check if there's at least one draft with DraftVisibleToAll = true AND no published status
    const hasDraftInReview = this.taxonomy.version_history_details?.some(
      (version: any) => version.status === 'draft' && version.DraftVisibleToAll === true
    );

    if (hasDraftInReview) {
      return 'Draft in Review';
    }

    return 'Draft';
  }

  // Method to get the tag class based on DraftVisibleToAll and published status
  getTagClass(): string {
    if (this.isPublished) {
      return 'published';
    }

    // Check if there's at least one draft with DraftVisibleToAll = true AND no published status
    const hasDraftInReview = this.taxonomy.version_history_details?.some(
      (version: any) => version.status === 'draft' && version.DraftVisibleToAll === true
    );

    if (hasDraftInReview) {
      return 'draft-in-review';
    }

    return 'draft';
  }

  handleMorePress() {
    this._showMoreDropdown.set(!this._showMoreDropdown());
  }

  handleDeleteTaxonomy = (event: MouseEvent, taxonomyId: string) => {
    const taxonomyIdForDelete = {
      taxonomy_id: taxonomyId,
    };
    this._showMoreDropdown.set(false);
    this.confirmPopupService.openConfirmDialog(PopupConfirmationData.deleteTaxonomyConfirmation).subscribe((result) => {
      if (result?.data?.confirm) {
        this.taxonomyApiService.deleteTaxonomyV2(taxonomyIdForDelete).subscribe(() => {
          this.taxonomyService.removeTaxonomyFromStorage(taxonomyId);
          this.taxonomyService.loadTaxonomy();
          this.taxonomyService.getTaxonomiesData();
        });
      }
    });
  };

  handleViewTaxonomy(
    event: MouseEvent,
    taxonomyId: string,
    taxonomyName: string,
    version_history_details: any,
    selected_taxonomy_version: any
  ) {
    // Fetch complete version history data to get all required fields
    this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: taxonomyId }).subscribe({
      next: (response) => {
        const complete_version_history_details = response.data.version_history_details || [];
        const complete_selected_taxonomy_version = complete_version_history_details[0] || null;

        const selectedValue = {
          taxonomy_id: taxonomyId,
          taxonomy_name: taxonomyName,
          version_history_details: complete_version_history_details,
          selected_taxonomy_version: complete_selected_taxonomy_version,
        };
        this.taxonomyService.setSelectedValue(selectedValue);
      },
      error: (err) => {
        console.error('Failed to fetch version history for taxonomy view:', err);
        // Fallback to using the provided data
        const selectedValue = {
          taxonomy_id: taxonomyId,
          taxonomy_name: taxonomyName,
          version_history_details: version_history_details || [],
          selected_taxonomy_version: selected_taxonomy_version || version_history_details?.[0] || null,
        };
        this.taxonomyService.setSelectedValue(selectedValue);
      },
    });
  }

  pressConfigureBtn(event: MouseEvent, taxonomyId: string) {
    sessionStorage.setItem('configureTaxonomyId', taxonomyId);

    this.actionService.openConfigureForm(taxonomyId);
  }

  onOverlayPress() {
    this.actionService.closeConfigureForm();
  }

  handleCloneTaxonomy(event: MouseEvent, taxonomyId: string, taxonomyName: string, versionHistoryDetails: any[]) {
    event.stopPropagation();

    this.clonePopupService
      .openCloneDialog({
        taxonomy_id: taxonomyId,
        taxonomy_name: taxonomyName,
        version_history_details: versionHistoryDetails,
      })
      .subscribe((result: any) => {
        if (result?.data?.confirm && result?.data?.cloneData) {
          const { taxonomy_id, version_id, taxonomy_name, taxonomy_description } = result.data.cloneData;

          this.taxonomyApiService
            .cloneTaxonomy({
              taxonomy_id,
              version_id,
              taxonomy_name,
              taxonomy_description,
            })
            .subscribe({
              next: (response) => {
                if (response?.success) {
                  this.notificationService.success('Clone Taxonomy', 'Taxonomy cloned successfully');
                  // Refresh the taxonomy list
                  this.taxonomyService.loadTaxonomy();
                  this.taxonomyService.getTaxonomiesData();
                } else {
                  this.notificationService.error('Clone Taxonomy', response?.message || 'Failed to clone taxonomy');
                }
              },
              error: (error) => {
                console.error('Error cloning taxonomy:', error);
                this.notificationService.error('Clone Taxonomy', 'Failed to clone taxonomy. Please try again.');
              },
            });
        }
      });
  }

  handleConfigureClick(event: MouseEvent, taxonomyId: string) {
    event.stopPropagation();
    this.moreMenuTrigger.closeMenu();
    this.pressConfigureBtn(event, taxonomyId);
  }

  handleCloneClick(event: MouseEvent, taxonomyId: string, taxonomyName: string, versionHistoryDetails: any[]) {
    event.stopPropagation();
    this.moreMenuTrigger.closeMenu();
    this.handleCloneTaxonomy(event, taxonomyId, taxonomyName, versionHistoryDetails);
  }
}
